/**
 * useApiDocumentation Hook - OpenAPI + API Keys (real backend).
 */

import { useCallback, useEffect, useState } from "react";
import { API_ENDPOINTS, apiFetchJson } from "@/lib/api";

export interface ApiEndpoint {
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
    path: string;
    description: string;
    tags: string[];
    security?: any[];
    parameters?: any[];
    responses: Array<{ status: number; description: string }>;
}

export interface ApiKey {
    id: number;
    name: string;
    key_prefix: string;
    scopes: string[];
    is_active: boolean;
    expires_at?: string | null;
}

export interface ScopeInfo {
    name: string;
    description: string;
}

export function useApiDocumentation() {
    const [endpoints, setEndpoints] = useState<ApiEndpoint[]>([]);
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [availableScopes, setAvailableScopes] = useState<ScopeInfo[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchApiDocumentation = useCallback(async () => {
        try {
            setError(null);
            const spec = await apiFetchJson<any>(API_ENDPOINTS.OPENAPI);
            const parsed: ApiEndpoint[] = [];

            Object.entries(spec?.paths || {}).forEach(([path, methods]: any) => {
                Object.entries(methods || {}).forEach(([method, op]: any) => {
                    parsed.push({
                        method: String(method).toUpperCase() as any,
                        path,
                        description: String(op?.summary || op?.description || ""),
                        tags: Array.isArray(op?.tags) ? op.tags : [],
                        security: Array.isArray(op?.security) ? op.security : [],
                        parameters: Array.isArray(op?.parameters)
                            ? op.parameters
                            : [],
                        responses: Object.entries(op?.responses || {}).map(
                            ([status, resp]: any) => ({
                                status: Number(status),
                                description: String(resp?.description || ""),
                            }),
                        ),
                    });
                });
            });
            setEndpoints(parsed);
        } catch (err) {
            console.error("Error fetching OpenAPI:", err);
            setEndpoints([]);
            setError(err instanceof Error ? err.message : "OpenAPI unavailable");
        }
    }, []);

    const fetchApiKeys = useCallback(async () => {
        try {
            const data = await apiFetchJson<any>(API_ENDPOINTS.API_KEYS_LIST);
            const list: ApiKey[] = Array.isArray(data) ? data : [];
            setApiKeys(list);
        } catch (err) {
            console.error("Error fetching API keys:", err);
            setApiKeys([]);
            setError(err instanceof Error ? err.message : "API keys unavailable");
        }
    }, []);

    const fetchAvailableScopes = useCallback(async () => {
        try {
            const data = await apiFetchJson<any>(API_ENDPOINTS.API_KEYS_SCOPES);
            const list = Array.isArray(data?.scopes) ? data.scopes : [];
            setAvailableScopes(
                list.map((s: any) => ({
                    name: String(s?.name || ""),
                    description: String(s?.description || ""),
                })),
            );
        } catch (err) {
            console.error("Error fetching scopes:", err);
            setAvailableScopes([]);
        }
    }, []);

    const createApiKey = useCallback(
        async (name: string, scopes: string[], expiresDays?: number) => {
            try {
                const res = await apiFetchJson<any>(API_ENDPOINTS.API_KEYS_CREATE, {
                    method: "POST",
                    json: {
                        name,
                        scopes,
                        expires_days: expiresDays ?? 365,
                    },
                });
                await fetchApiKeys();
                return { success: true, api_key: res?.api_key as string };
            } catch (err) {
                return {
                    success: false,
                    error: err instanceof Error ? err.message : "Unknown error",
                };
            }
        },
        [fetchApiKeys],
    );

    const revokeApiKey = useCallback(
        async (keyId: number) => {
            try {
                await apiFetchJson<any>(API_ENDPOINTS.API_KEYS_DELETE(keyId), {
                    method: "DELETE",
                });
                await fetchApiKeys();
                return { success: true };
            } catch (err) {
                return {
                    success: false,
                    error: err instanceof Error ? err.message : "Unknown error",
                };
            }
        },
        [fetchApiKeys],
    );

    const refresh = useCallback(async () => {
        setIsLoading(true);
        await Promise.all([
            fetchApiDocumentation(),
            fetchApiKeys(),
            fetchAvailableScopes(),
        ]);
        setIsLoading(false);
    }, [fetchApiDocumentation, fetchApiKeys, fetchAvailableScopes]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return {
        endpoints,
        apiKeys,
        availableScopes,
        isLoading,
        error,
        createApiKey,
        revokeApiKey,
        refresh,
    };
}
